import {
  Component,
  EventEmitter,
  inject,
  Input,
  Output,
  DestroyRef,
  ChangeDetectorRef
} from '@angular/core';
import { PitchCardModalBase } from '../pitchcard-modal-base';
import { CommonModule } from '@angular/common';
import { ElementRef, ViewChild } from '@angular/core';
import { takeWhile } from 'rxjs';
import { DialogModule } from 'primeng/dialog';
import { ButtonDirective } from 'primeng/button';
import { TranslateModule } from '@ngx-translate/core';
import { ConfirmationService } from 'primeng/api';
import { BusinessReviewsComponent } from '../../../business-reviews/business-reviews.component';
import { UploadVideoComponent } from '@App/modules/pitch-card-shared/pages/upload-video-steps/upload-video-steps.component';
import { BusinessService, UserCommonApiService } from '@App/common/services';
import {
  CommonBindingDataService,
  NotificationService
} from '@Shared/services';
import { LoaderService } from '@Shared/components/loader/loader.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CustomerAnalyticsService } from '../../../../../choosen-history/services';
import { PitchCardModalsWrapperService } from '@App/modules/pitch-card-shared/services';
import { AuthService } from '@App/pages/auth/services';
import { isPlatformBrowser } from '@angular/common';
import { PLATFORM_ID } from '@angular/core';
import { notifyModalError, notifyModalSuccess } from '../modal-feedback';

@Component({
  selector: 'app-pitchcard-video-reviews-modal',
  standalone: true,

  imports: [
    CommonModule,
    DialogModule,
    ButtonDirective,
    TranslateModule,
    BusinessReviewsComponent,
    UploadVideoComponent
  ],
  templateUrl: './pitchcard-video-reviews-modal.component.html'
})
export class PitchCardVideoReviewsModalComponent extends PitchCardModalBase {
  @Input() showViewReviewsDialog: any;
  @Input() showUploadReviewVideoDialog: any;
  @Input() currentPitchCard: any;
  @Input() learnAboutMeReviews: any;
  @Output() playVideo = new EventEmitter<string>();
  @Output() reviewsChange = new EventEmitter<any[]>();
  @Output() businessRefresh = new EventEmitter<void>();

  showUploadReviewRatingScreen = false;
  uploadedReviewVideoFile: any;
  uploadReviewVideoMetaData: any;
  isCoverLandscape = true;
  coverImageHeight = '';
  @ViewChild('coverImage') coverImage: ElementRef;
  private uploadingVideoId: string;
  private businessService = inject(BusinessService);
  private userCommonService = inject(UserCommonApiService);
  private commonBindingService = inject(CommonBindingDataService);
  private notificationService = inject(NotificationService);
  private loaderService = inject(LoaderService);
  private customerAnalyticsService = inject(CustomerAnalyticsService);
  private pitchCardModalsWrapperService = inject(PitchCardModalsWrapperService);
  private confirmationService = inject(ConfirmationService);
  private authService = inject(AuthService);
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);
  private platformId = inject(PLATFORM_ID);
  resetUpload() {
    this.undoUploadedVideoReview();
  }

  handleLeaveReviewClick() {
    this.pitchCardModalsWrapperService.onTitleClick('leaveareview');
  }

  moveToUploadReviewRatingScreen(event: any) {
    this.loaderService.show();
    this.uploadedReviewVideoFile = event.uploadedFiles[0];
    this.uploadingVideoId = event.uploadedFiles[0].fileId;

    const requestCallback = () => {
      this.onFlag('showUploadReviewRatingScreen', true);
      this.loaderService.hide();
      this.cdr.detectChanges();
    };

    this.fetchingVideoAPI(requestCallback, 1000);
  }

  updateUploadedReviewVideo() {
    this.confirmationService.confirm({
      message: this.commonBindingService.getLabel('confirm_reupload_video'),
      accept: () => {
        this.undoUploadedVideoReview();
      }
    });
  }

  undoUploadedVideoReview() {
    this.uploadReviewVideoMetaData = {};
    this.uploadedReviewVideoFile = {};
    this.uploadingVideoId = null;
    this.onFlag('showUploadReviewRatingScreen', false);
    this.cdr.detectChanges();
  }

  calcCover() {
    this.isCoverLandscape =
      this.coverImage.nativeElement.naturalWidth /
        this.coverImage.nativeElement.naturalHeight >
      1.33;
    this.coverImageHeight =
      (this.coverImage.nativeElement.width / 12) * 9 + 'px';
  }

  submitUploadedVideoReview() {
    if (!this.uploadingVideoId) {
      this.notificationService.showNotification(
        'ERROR',
        'Video ID is missing. Please try uploading again.',
        'main',
        3000
      );
      return;
    }

    const params = this.createSubmitVideoReviewParams();
    this.sendAddVideoReviewRequest(params);
    this.onFlag('showUploadReviewVideoDialog', false);
    this.cdr.detectChanges();
  }

  createSubmitVideoReviewParams() {
    if (
      !this.currentPitchCard?.id ||
      !this.userCommonService.userId ||
      !this.uploadingVideoId
    ) {
      console.error(
        'Missing required parameters for video review submission:',
        {
          businessId: this.currentPitchCard?.id,
          userId: this.userCommonService.userId,
          videoReviewId: this.uploadingVideoId
        }
      );
      throw new Error(
        'Missing required parameters for video review submission'
      );
    }

    return {
      businessId: this.currentPitchCard.id,
      userId: this.userCommonService.userId,
      videoReviewId: this.uploadingVideoId,
      pitchCardType: this.currentPitchCard.businessType
    };
  }

  sendAddVideoReviewRequest(params: any) {
    this.loaderService.show();

    this.customerAnalyticsService.addReview(params).subscribe({
      next: () => {
        this.customerAnalyticsService
          .getBusinessVideoReviewList(this.currentPitchCard.id, {
            limit: null,
            offset: null
          })
          .subscribe({
            next: ({ data }) => {
              this.learnAboutMeReviews = [...data.reviews];
              this.reviewsChange.emit(this.learnAboutMeReviews);
              this.loaderService.hide();
              notifyModalSuccess(
                this.notificationService,
                'Video testimonial submitted successfully!'
              );
              this.currentPitchCard =
                this.pitchCardModalsWrapperService.getCurrentBusiness();
              this.businessRefresh.emit();
              this.cdr.detectChanges();
            },
            error: (error) => {
              this.loaderService.hide();
              notifyModalError(this.notificationService, error);
            }
          });
      },
      error: (error) => {
        this.loaderService.hide();
        notifyModalError(this.notificationService, error);
      }
    });
  }

  fetchingVideoAPI(callback: any, milliSeconds: number) {
    setTimeout(() => {
      this.fetchMuxVideoData(callback);
    }, milliSeconds);
  }

  fetchMuxVideoData(callback: any) {
    if (this.uploadedReviewVideoFile) {
      this.businessService
        .getVideoDetail(this.uploadedReviewVideoFile.fileId)
        .subscribe({
          next: (result) => {
            if (result?.data && result?.data?.status !== 'errored') {
              this.handleFetchMuxSuccessRequest(result.data, callback);
            } else {
              const err = {
                Message: this.commonBindingService.getLabel(
                  'msg_video_details_fetch_error'
                )
              };
              notifyModalError(this.notificationService, err);
            }
          },
          error: () => {
            const err = {
              Message: this.commonBindingService.getLabel(
                'msg_video_details_fetch_error'
              )
            };
            notifyModalError(this.notificationService, err);
          }
        });
    }
  }

  handleFetchMuxSuccessRequest(metaData: any, callback: any) {
    this.uploadReviewVideoMetaData = metaData;

    if (this.uploadReviewVideoMetaData.status !== 'ready') {
      this.loaderService.show();
      this.fetchingVideoAPI(callback, 3000);
      return;
    }

    callback();
    this.cdr.detectChanges();
  }

  uploadVideoFile(fileForUpload: File) {
    this.businessService
      .createVideoAsset(isPlatformBrowser(this.platformId) ? window.origin : '')
      .subscribe({
        next: ({ data }) => {
          this.userCommonService
            .uploadMuxFile(data, fileForUpload)
            .pipe(
              takeWhile((response) => response?.status !== 'ready'), // Take responses until status is 'ready'
              takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
              next: () => {
                // for loader data
                this.uploadingVideoId = data.id;
              },
              complete: () => {
                this.fetchMuxVideoTestimonialsData();
              }
            });
        },
        error: ({ message, code }) => {
          this.loaderService.hide();
          if (code === 1002) {
            this.onFlag('showLearnAboutMeDialog', false);
            this.authService.setAction('SIGN_UP');
            this.notificationService.clearNotifications();
          } else {
            this.notificationService.showNotification('ERROR', message);
          }
        }
      });
  }

  fetchMuxVideoTestimonialsData() {
    this.businessService.getVideoDetail(this.uploadingVideoId).subscribe({
      next: (metaDataResponse) => {
        if (metaDataResponse?.data?.status === 'ready') {
          // return this.onUpload(metaDataResponse);
          this.submitUploadedVideoReview();
          this.currentPitchCard =
            this.pitchCardModalsWrapperService.getCurrentBusiness();
        } else {
          this.pollMuxAPI(4000);
        }
      },
      error: () => {
        this.loaderService.hide();
        this.notificationService.showNotification(
          'ERROR',
          this.commonBindingService.getLabel('msg_video_details_fetch_error')
        );
      }
    });
  }

  pollMuxAPI(milli: number) {
    setTimeout(() => {
      this.fetchMuxVideoTestimonialsData();
    }, milli);
  }
}
